import {createStore as createReduxStore, applyMiddleware, compose} from 'redux'
import {Provider} from 'react-redux'
import {PersistGate} from 'redux-persist/integration/react'
import {persistCombineReducers, persistStore} from 'redux-persist'
import baseReducers from './reducers'
import Localforage from 'localforage'
import configureReactotron from './configure_reactotron'
import {sizeMonitorMiddleware} from './size_monitor_middleware'
import {createSentryReduxEnhancer} from './sentry_redux_enhancer'

export const createStore = ({
	key,
	reducers,
	storage = Localforage,
	sentryRedux,
	persistConfig,
	setupReactotron,
	enableLogoutReset,
	enableSizeMonitor,
	keepOnLogout = [], // keys to keep on logout, e.g. ['persisted']
}) => {
	const reactotron = configureReactotron(key, setupReactotron)
	const makeStore = reactotron ? reactotron.createStore : createReduxStore
	const config = {key, storage, ...persistConfig}

	const persistedReducers = persistCombineReducers(config, {...baseReducers, ...reducers})

	const rootReducer = (state, action) => {
		if (enableLogoutReset && action.type === 'LOGOUT') {
			// Reset store to initial state, but reserve _persist so redux-persist keeps working
			const {_persist} = state || {}
			const newState = persistedReducers(undefined, {type: '@@INIT'})
			keepOnLogout.forEach(key => {
				if (state && state[key]) newState[key] = state[key]
			})
			return {...newState, _persist}
		}
		return persistedReducers(state, action)
	}

	// Conditionally add size monitor middleware and compose with any provided enhancer
	const sizeEnhancer = enableSizeMonitor ? applyMiddleware(sizeMonitorMiddleware) : undefined
	const sentryEnhancer = createSentryReduxEnhancer({appKey: key, ...sentryRedux})
	const finalEnhancer = [sizeEnhancer, sentryEnhancer].filter(Boolean)
	const composedEnhancer = finalEnhancer.length ? compose(...finalEnhancer) : undefined

	const store = makeStore(rootReducer, undefined, composedEnhancer)

	const persistor = persistStore(store, null, () => {
		store.getState()
	})

	const replaceReducers = newReducers => {
		store.replaceReducer(persistCombineReducers(config, {...baseReducers, ...newReducers}))
	}

	return {store, persistor, replaceReducers}
}

export const StoreProvider = ({store, persistor, children}) => (
	<Provider store={store}>
		<PersistGate persistor={persistor} loading={null}>
			{children}
		</PersistGate>
	</Provider>
)
